import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import "./index.css";
import { ThemeProvider } from "./lib/theme/ThemeContext";
import "@/lib/styles/theme.css"; // This now imports all the modularized files through the main theme.css
import * as Sentry from "@sentry/react";

// Only enable Sentry in production
// This is the most effective way to prevent console spam in development
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://de9329e8be23573b96edd4df42a2518c@o365574.ingest.us.sentry.io/4509036038782976",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.2, // Capture 20% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample rate at which sessions should be recorded
    replaysOnErrorSampleRate: 1.0, // Sample rate at which sessions that have errors should be recorded
    environment: "production",
    debug: false,
    // Completely disable console by overriding the console in the browser
    beforeBreadcrumb(breadcrumb) {
      // Don't capture console breadcrumbs at all
      if (breadcrumb.category === 'console') {
        return null;
      }
      return breadcrumb;
    },
    // Prevent recursion
    beforeSend(event, hint) {
      const error = hint.originalException || hint.syntheticException;
      
      // Type guards for error detection
      const isErrorLike = (err: unknown): err is { message?: string; name?: string; stack?: string } => {
        return err !== null && typeof err === 'object';
      };
      
      // Extract error information safely using type guards
      let errorName = '';
      let errorMessage = '';
      let errorStack = '';
      
      if (isErrorLike(error)) {
        errorName = error.name || '';
        errorMessage = error.message || String(error);
        errorStack = error.stack || '';
      } else {
        errorMessage = String(error);
      }
      
      // Check if the error is related to Sentry or might cause recursion
      if (
        errorName.includes('Sentry') || 
        errorMessage.includes('Sentry') ||
        errorMessage.includes('infinite') ||
        errorMessage.includes('recursion') ||
        errorStack.includes('sentry')
      ) {
        return null;
      }
      
      return event;
    },
  });
} else {
  // In development, initialize with an empty DSN to completely disable Sentry
  Sentry.init({
    enabled: false,
    dsn: ""
  });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </React.StrictMode>
);
